import { useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from '@/utils/debounce/debounce';

export const SEARCH_DELAY = 500;

type QueryFilters = { query?: string };

/**
 * Search-box state for a filter toolbar. The debouncer reads props through a ref so it is never
 * rebuilt and commits the filters as they are when the timer fires, not as they were at keypress.
 * `alsoClear` is merged into every commit, for toolbars where search invalidates another control.
 * A pending commit is dropped on unmount.
 */
export function useDebouncedFilterQuery<T extends QueryFilters>(
  filters: T | undefined,
  onChangeFilters: (filters: T) => void,
  alsoClear?: Partial<T>
) {
  const [filterValue, setFilterValue] = useState(filters?.query ?? '');
  const committed = useRef<string | undefined>(undefined);

  // Re-sync only from a writer that is not this box. Echoing our own commit back would overwrite
  // whatever was typed while the parent was still rendering it.
  useEffect(() => {
    const incoming = filters?.query ?? '';
    if (incoming === committed.current) return;

    setFilterValue(incoming);
  }, [filters?.query]);

  const latest = useRef({ filters, onChangeFilters, alsoClear });
  useEffect(() => {
    latest.current = { filters, onChangeFilters, alsoClear };
  });

  const commit = useMemo(
    () =>
      debounce((query: string) => {
        const current = latest.current;
        committed.current = query;
        current.onChangeFilters({ ...(current.filters as T), ...current.alsoClear, query });
      }, SEARCH_DELAY),
    []
  );

  // A toolbar unmounts the moment its page renders an outlet, and the commit would then write
  // filters onto whatever route replaced it.
  useEffect(() => commit.cancel, [commit]);

  const onQueryChange = (value: string) => {
    setFilterValue(value);
    commit(value);
  };

  return { filterValue, onQueryChange };
}
